import { ref } from 'vue';
import useRotas from './useRotas';

const STORAGE_KEY_PREFIX = 'leilao_scroll_state_';
const STATE_VALIDITY_TIME = 60 * 60 * 1000; // 1 hora em milissegundos

export default function useListagemLotesInfinita(leilao) {
    const route = useRotas();

    const infiniteScroll = ref(null);
    const tudoCarregado = ref(false);
    const lotesCarregados = ref([]);
    const currentPage = ref(0);
    const restaurando = ref(true); // Começar como true para evitar carregamento antes da restauração

    const getStorageKey = () => {
        return `${STORAGE_KEY_PREFIX}${leilao.value.id}`;
    };

    const saveScrollState = (loteId) => {
        const state = {
            page: currentPage.value,
            loteId: loteId,
            timestamp: Date.now()
        };
        localStorage.setItem(getStorageKey(), JSON.stringify(state));
    };

    const getSavedScrollState = () => {
        try {
            const saved = localStorage.getItem(getStorageKey());
            if (!saved) return null;

            const state = JSON.parse(saved);
            const now = Date.now();
            
            // Verificar se o estado ainda é válido (menos de 1 hora)
            if (now - state.timestamp > STATE_VALIDITY_TIME) {
                localStorage.removeItem(getStorageKey());
                return null;
            }

            return state;
        } catch (e) {
            console.error('Erro ao ler estado do scroll:', e);
            return null;
        }
    };

    const clearScrollState = () => {
        localStorage.removeItem(getStorageKey());
    };

    const loadData = async (index, done) => {
        // Não carregar durante a restauração
        if (restaurando.value) {
            done();
            return;
        }

        // Verificar se este índice já foi carregado (pode acontecer após restauração)
        // Se o índice solicitado é menor ou igual ao currentPage, significa que já foi carregado
        if (index <= currentPage.value) {
            console.log(`Índice ${index} já foi carregado (currentPage: ${currentPage.value}), ignorando...`);
            done();
            return;
        }

        // console.log('Trigger Scroll...');
        if (!tudoCarregado.value){
            console.log('Requisitando lotes...');
            // Converter índice do infinite scroll (0-based) para número da página da API (1-based)
            const pageNumber = index - 1;
            const lotes = await (await fetch(route('leilao.lotes', {
                idLeilao: leilao.value.id,
                _query: {
                    page: pageNumber
                }
            }), {
                method: 'GET',
                credentials: 'include'
            })).json();
            // console.log(`${lotes.length} Lotes carregados...`);
            lotesCarregados.value.push(...lotes)
            currentPage.value = index;
            if (lotes.length === 0){
                // console.log('Lotes esgotados...');
                tudoCarregado.value = true;
                if (infiniteScroll.value) {
                    infiniteScroll.value.stop()
                }
            }
            done()
        } else {
            // console.log('Sem mais lotes...');
            if (infiniteScroll.value) {
                infiniteScroll.value.stop()
            }
            done()
        }
    }

    const restoreScrollState = async () => {
        const savedState = getSavedScrollState();
        if (!savedState) {
            // Se não há estado para restaurar, permitir carregamento normal
            restaurando.value = false;
            return null;
        }

        restaurando.value = true;

        // Parar o infinite scroll durante a restauração
        if (infiniteScroll.value) {
            infiniteScroll.value.stop();
        }

        // savedState.page é o índice do infinite scroll (0, 1, 2...)
        // Precisamos carregar todas as páginas da API (1-based) de 1 até (savedState.page + 1)
        const targetScrollIndex = savedState.page;
        const promises = [];

        // Carregar todas as páginas da API de 1 até (targetScrollIndex) 
        // Pois índice 0 = página 1, índice 1 = página 2, etc.
        for (let apiPage = 1; apiPage < targetScrollIndex; apiPage++) {
            promises.push(
                fetch(route('leilao.lotes', {
                    idLeilao: leilao.value.id,
                    _query: { page: apiPage }
                }), {
                    method: 'GET',
                    credentials: 'include'
                }).then(res => res.json())
            );
        }

        try {
            const allLotes = await Promise.all(promises);
            // Limpar lotes existentes antes de adicionar os restaurados
            lotesCarregados.value = [];
            allLotes.forEach(lotes => {
                if (lotes.length > 0) {
                    lotesCarregados.value.push(...lotes);
                }
            });
            // Manter o índice do infinite scroll
            currentPage.value = targetScrollIndex;

            // Verificar se ainda há mais páginas
            const lastPageLotes = allLotes[allLotes.length - 1];
            if (lastPageLotes.length === 0) {
                tudoCarregado.value = true;
            } else {
                // Se ainda há lotes, reativar o infinite scroll
                tudoCarregado.value = false;
            }

            restaurando.value = false;

            return savedState.loteId;
        } catch (e) {
            console.error('Erro ao restaurar estado do scroll:', e);
            clearScrollState();
            restaurando.value = false;
            return null;
        }
    };

    return {
        infiniteScroll,
        tudoCarregado,
        lotesCarregados,
        loadData,
        saveScrollState,
        getSavedScrollState,
        clearScrollState,
        restoreScrollState,
        currentPage
    }
}
